import React from 'react';
import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

type GuardianProfileActionHeaderRightAction = {
  label: string;
  onPress: () => void;
  accessibilityLabel?: string;
  showOnTablet?: boolean;
  disabled?: boolean;
};

type GuardianProfileActionHeaderProps = {
  title: string;
  isTablet: boolean;
  topInset: number;
  onBack: () => void;
  backLabel?: string;
  rightAction?: GuardianProfileActionHeaderRightAction;
};

export function GuardianProfileActionHeader({
  title,
  isTablet,
  topInset,
  onBack,
  backLabel = STRINGS.common.buttons.back,
  rightAction,
}: GuardianProfileActionHeaderProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const visibleRightAction =
    rightAction && (!isTablet || rightAction.showOnTablet) ? rightAction : null;
  const rightActionDisabled = visibleRightAction?.disabled === true;

  return (
    <Stack
      direction="row"
      align="center"
      backgroundColor={isTablet ? colors['0'] : guardianColors['1']}
      paddingTop={topInset}
      minHeight={isTablet ? topInset + scale(64) : undefined}
      paddingBottom={isTablet ? scale(0) : scale(8)}
      paddingX={scale(20)}>
      <Stack flex={1} direction="row" align="center">
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={backLabel}
          onPress={onBack}
          style={({ pressed }) => [
            isTablet ? styles.tabletBackButton : styles.mobileBackButton,
            pressed ? styles.pressed : null,
          ]}>
          <SvgIcon
            name="IcBackBlue"
            size={scale(24)}
            color={isTablet ? guardianColors['Main/Main'] : guardianColors['10']}
          />
          {isTablet ? (
            <Text variant="Font Guardian App/Subtitle/Subtitle 14 SemiBold" style={styles.backText}>
              {backLabel}
            </Text>
          ) : null}
        </Pressable>
      </Stack>

      <Stack flex={3} align="center" justify="center" minWidth={0}>
        <Text
          variant={
            isTablet ? 'Font Inter/Subtitle/Bold 14px' : 'Font Guardian App/Body/Body 16 Bold'
          }
          style={[styles.headerTitle, isTablet ? styles.headerTitleTablet : null]}>
          {title}
        </Text>
      </Stack>

      <Stack flex={1} direction="row" justify="flex-end">
        {visibleRightAction ? (
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ disabled: rightActionDisabled }}
            accessibilityLabel={visibleRightAction.accessibilityLabel ?? visibleRightAction.label}
            disabled={rightActionDisabled}
            onPress={visibleRightAction.onPress}
            style={({ pressed }) => [
              styles.saveButton,
              pressed && !rightActionDisabled ? styles.pressed : null,
            ]}>
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
              style={[styles.saveText, rightActionDisabled ? styles.saveTextDisabled : null]}>
              {visibleRightAction.label}
            </Text>
          </Pressable>
        ) : null}
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  mobileBackButton: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    borderCurve: 'continuous',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors['0'],
  },
  tabletBackButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  pressed: {
    opacity: 0.72,
  },
  backText: {
    color: guardianColors['Main/Main'],
  },
  headerTitle: {
    color: guardianColors['10'],
    textAlign: 'center',
  },
  headerTitleTablet: {
    color: guardianColors['Main/Main'],
  },
  saveButton: {
    minHeight: scale(40),
    justifyContent: 'center',
    paddingLeft: scale(12),
  },
  saveText: {
    color: guardianColors['Main/Main'],
  },
  saveTextDisabled: {
    color: guardianColors['4'],
  },
}));
